import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Person,
  Email,
  Phone,
  Checkroom,
  Edit,
  Save,
  Cancel,
  // AdminPanelSettings,
  // People,
  // School,
  // Payment
} from '@mui/icons-material';
import apiClient from '../../config/axios';
import { useAuth } from '../../contexts/AuthContext';
import AvatarEditor from '../../components/AvatarEditor';

const AdminProfile = () => {
  const { user, updateUser, loading: authLoading, error: authError } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    surname: user?.surname || '',
    telephone: user?.telephone || '',
    telephoneSecondary: user?.telephoneSecondary || '',
    shirtSize: user?.shirtSize || '',
    avatar: user?.avatar || ''
  });
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState(null);
  const [selectedAvatar, setSelectedAvatar] = useState(formData.avatar || user.avatar || '');

  const shirtSizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const numAvatars = 9;
  const availableAvatars = Array.from({ length: numAvatars }, (_, i) => `default${i + 1}.jpg`);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAvatarSave = async () => {
    setAvatarLoading(true);
    setAvatarError(null);
    try {
      const response = await apiClient.patch('/api/user/update-user', { avatar: selectedAvatar });
      updateUser({ ...user, ...response.data });
      setFormData(prev => ({ ...prev, avatar: selectedAvatar }));
      setAvatarModalOpen(false);
    } catch (error) {
      setAvatarError(error.response?.data?.message || 'Errore durante il salvataggio dell\'immagine');
    } finally {
      setAvatarLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setFormData({
      name: user?.name || '',
      surname: user?.surname || '',
      telephone: user?.telephone || '',
      telephoneSecondary: user?.telephoneSecondary || '',
      shirtSize: user?.shirtSize || '',
      avatar: user?.avatar || ''
    });
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.patch('/api/user/update-user', formData);
      updateUser({ ...user, ...response.data });
      setSuccess('Profilo aggiornato con successo!');
      setIsEditing(false);
    } catch (error) {
      setError(error.response?.data?.message || 'Errore durante l\'aggiornamento del profilo');
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Alert severity="error">
            {authError || "Utente non autenticato. Effettua il login."}
          </Alert>
        </Box>
      </Container>
    );
  }

  // Check if user is admin
  if (!user.roles?.includes('admin')) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Alert severity="error">
            Accesso negato. Solo gli amministratori possono accedere a questa pagina.
          </Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom>
          Profilo Amministratore
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Avatar - appears first on mobile */}
          <Grid size={{ xs: 12, md: 3 }} sx={{ order: { xs: 1, md: 2 } }}>
            <Grid container spacing={2}>
              {/* User Avatar and Roles */}
              <Grid size={12}>
                <Card>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Avatar
                      src={formData.avatar || user.avatar ? (formData.avatar || user.avatar).startsWith('data:image/') ? (formData.avatar || user.avatar) : `/avatars/${formData.avatar || user.avatar}` : ''}
                      sx={{ width: 100, height: 100, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: '2rem' }}
                    >
                      {!(formData.avatar || user.avatar) && '?'}
                    </Avatar>
                    <Box sx={{ mb: 2 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        sx={{ minHeight: 24, minWidth: 24, px: 1, py: 0, fontSize: '0.75rem', lineHeight: 1 }}
                        onClick={() => setAvatarModalOpen(true)}
                      >
                        Cambia immagine
                      </Button>
                    </Box>
                    <Dialog open={avatarModalOpen} onClose={() => setAvatarModalOpen(false)}>
                      <DialogTitle>Modifica immagine profilo</DialogTitle>
                      <DialogContent>
                        <AvatarEditor
                          value={formData.avatar || user.avatar || ''}
                          availableAvatars={availableAvatars}
                          onSelectedAvatarChange={setSelectedAvatar}
                        />
                        {avatarLoading && (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <CircularProgress size={24} />
                          </Box>
                        )}
                        {avatarError && (
                          <Alert severity="error" sx={{ mt: 2 }}>{avatarError}</Alert>
                        )}
                      </DialogContent>
                      <DialogActions>
                        <Button
                          onClick={() => setAvatarModalOpen(false)}
                          disabled={avatarLoading}
                        >
                          Annulla
                        </Button>
                        <Button
                          onClick={handleAvatarSave}
                          disabled={avatarLoading || !selectedAvatar || selectedAvatar === (formData.avatar || user.avatar || '')}
                        >
                          Salva
                        </Button>
                      </DialogActions>
                    </Dialog>
                    <Typography variant="h6" gutterBottom>
                      {user.name} {user.surname}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, flexWrap: 'wrap' }}>
                      {user.roles?.map((role) => (
                        <Chip
                          key={role}
                          label={role === 'admin' ? 'Amministratore' : role === 'teacher' ? 'Docente' : 'Membro'}
                          color={role === 'admin' ? 'error' : role === 'teacher' ? 'warning' : 'primary'}
                          size="small"
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Grid>

          {/* Profile Information - appears second on mobile */}
          <Grid size={{ xs: 12, md: 9 }} sx={{ order: { xs: 2, md: 1 } }}>
            <Paper elevation={2} sx={{ p: 3 }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3,
                // Hide buttons on mobile, show only on desktop
                '& .desktop-buttons': {
                  display: { xs: 'none', md: 'block' }
                }
              }}>
                <Typography variant="h6">
                  Informazioni Personali
                </Typography>
                <Box className="desktop-buttons">
                  {!isEditing ? (
                    <Button
                      startIcon={<Edit />}
                      onClick={handleEdit}
                      variant="outlined"
                    >
                      Modifica
                    </Button>
                  ) : (
                    <Box>
                      <Button
                        startIcon={<Save />}
                        onClick={handleSave}
                        variant="contained"
                        disabled={loading}
                        sx={{ mr: 1 }}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Salva'}
                      </Button>
                      <Button
                        startIcon={<Cancel />}
                        onClick={handleCancel}
                        variant="outlined"
                      >
                        Annulla
                      </Button>
                    </Box>
                  )}
                </Box>
              </Box>

              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Nome"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    disabled={!isEditing}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Cognome"
                    name="surname"
                    value={formData.surname}
                    onChange={handleChange}
                    disabled={!isEditing}
                  />
                </Grid>
                <Grid size={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={user.email}
                    disabled
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Telefono"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleChange}
                    disabled={!isEditing}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Telefono Secondario"
                    name="telephoneSecondary"
                    value={formData.telephoneSecondary}
                    onChange={handleChange}
                    disabled={!isEditing}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    select
                    label="Taglia Maglietta"
                    name="shirtSize"
                    value={formData.shirtSize}
                    onChange={handleChange}
                    disabled={!isEditing}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Checkroom color="action" />
                          </InputAdornment>
                        ),
                      },
                    }}
                  >
                    {shirtSizes.map((size) => (
                      <MenuItem key={size} value={size}>
                        {size}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              {/* Mobile buttons at the bottom */}
              <Box sx={{ 
                display: { xs: 'flex', md: 'none' }, 
                justifyContent: 'center',
                gap: 2,
                mt: 3,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                {!isEditing ? (
                  <Button
                    startIcon={<Edit />}
                    onClick={handleEdit}
                    variant="contained"
                    fullWidth
                    size="large"
                  >
                    Modifica
                  </Button>
                ) : (
                  <>
                    <Button
                      startIcon={<Save />}
                      onClick={handleSave}
                      variant="contained"
                      disabled={loading}
                      fullWidth
                      size="large"
                    >
                      {loading ? <CircularProgress size={20} /> : 'Salva'}
                    </Button>
                    <Button
                      startIcon={<Cancel />}
                      onClick={handleCancel}
                      variant="outlined"
                      fullWidth
                      size="large"
                    >
                      Annulla
                    </Button>
                  </>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

export default AdminProfile; 